import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { Layout } from '../components/Layout';
import { config } from '../config';
import { useDeveloperInfo } from '../hooks/useApi';
import {
  getEndpointSlug,
  getQuotaUrl,
  getServiceBaseUrl,
} from '../utils/docs';
import type { ApiEndpoint, QuotaResponse } from '../types';
import {
  isTryOutSlug,
  tryOutInstructions,
  tryOutKind,
  tryOutPageTitle,
} from '../tryout/registry';
import {
  loadHistory,
  prependHistory,
  updateHistoryEntry,
  type TryOutHistoryEntry,
} from '../tryout/history';
import { pollUntilDone } from '../tryout/poll';

type Phase = 'idle' | 'submitting' | 'polling' | 'completed' | 'error';

function formatJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export function TryOutPage() {
  const { slug: slugParam } = useParams<{ slug: string }>();
  const slug = slugParam ?? '';
  const { data, isLoading: infoLoading, error: infoError } = useDeveloperInfo();

  const [quota, setQuota] = useState<QuotaResponse | null>(null);
  const [quotaLoading, setQuotaLoading] = useState(false);
  const [quotaError, setQuotaError] = useState(false);

  const [history, setHistory] = useState<TryOutHistoryEntry[]>([]);
  const [parsedBody, setParsedBody] = useState<unknown | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [fileLabel, setFileLabel] = useState<string>('');

  const [phase, setPhase] = useState<Phase>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [lastCallAt, setLastCallAt] = useState<string | null>(null);
  const [rawResult, setRawResult] = useState<string>('');
  /** `null` = "New request" selected; otherwise a request id from history. */
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null);
  const [historyFetchLoading, setHistoryFetchLoading] = useState(false);
  const [historyFetchError, setHistoryFetchError] = useState<string | null>(null);
  const [historyFetchedRaw, setHistoryFetchedRaw] = useState<string>('');
  /** After Submit is used once, upload stays off until "New request". */
  const [fileWasSent, setFileWasSent] = useState(false);
  /** Remount file input so the same file can be selected again after reset. */
  const [fileInputKey, setFileInputKey] = useState(0);

  const pollAbortRef = useRef<AbortController | null>(null);
  /** Lets the AbortError path skip state updates when abort was intentional (new submit or "New request"). */
  const pollAbortReasonRef = useRef<'replace' | 'new-request' | null>(null);
  const selectedHistoryIdRef = useRef<string | null>(null);
  selectedHistoryIdRef.current = selectedHistoryId;

  const endpoints = (data?.endpoints ?? []) as ApiEndpoint[];
  const selectedEndpoint = useMemo(
    () =>
      endpoints.find(
        (ep) =>
          getEndpointSlug(ep.docsUrl ?? ep.documentation_url ?? '').toLowerCase() ===
          slug.toLowerCase()
      ),
    [endpoints, slug]
  );

  const apiKey = data?.user.apiKey ?? '';
  const serviceBase = selectedEndpoint
    ? getServiceBaseUrl(
      selectedEndpoint.docsUrl ?? selectedEndpoint.documentation_url ?? '',
      config.apiUrl
    )
    : '';
  const quotaUrl = selectedEndpoint
    ? getQuotaUrl(
      selectedEndpoint.docsUrl ?? selectedEndpoint.documentation_url ?? '',
      config.apiUrl
    )
    : '';

  const fetchQuota = useCallback(async () => {
    if (!quotaUrl || !apiKey) return;
    setQuotaLoading(true);
    setQuotaError(false);
    try {
      const res = await fetch(quotaUrl, { headers: { 'x-api-key': apiKey } });
      if (!res.ok) throw new Error('quota');
      const json = (await res.json()) as QuotaResponse;
      setQuota(json);
    } catch {
      setQuotaError(true);
      setQuota(null);
    } finally {
      setQuotaLoading(false);
    }
  }, [quotaUrl, apiKey]);

  useEffect(() => {
    if (!isTryOutSlug(slug) || !selectedEndpoint || !apiKey) return;
    fetchQuota();
  }, [slug, selectedEndpoint, apiKey, fetchQuota]);

  useEffect(() => {
    if (phase !== 'polling' || !quotaUrl || !apiKey) return;
    const t = window.setInterval(() => {
      void fetchQuota();
    }, 10_000);
    return () => window.clearInterval(t);
  }, [phase, quotaUrl, apiKey, fetchQuota]);

  useEffect(() => {
    if (!isTryOutSlug(slug)) return;
    setHistory(loadHistory(slug));
  }, [slug]);

  useEffect(() => {
    return () => {
      pollAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!selectedHistoryId || !serviceBase || !apiKey) {
      setHistoryFetchLoading(false);
      setHistoryFetchError(null);
      setHistoryFetchedRaw('');
      return;
    }

    const ac = new AbortController();
    setHistoryFetchLoading(true);
    setHistoryFetchError(null);
    setHistoryFetchedRaw('');

    const url = `${serviceBase.replace(/\/$/, '')}/requests/${encodeURIComponent(selectedHistoryId)}`;
    fetch(url, { headers: { 'x-api-key': apiKey }, signal: ac.signal })
      .then(async (res) => {
        const text = await res.text();
        let parsed: unknown;
        try {
          parsed = text ? JSON.parse(text) : null;
        } catch {
          parsed = { _raw: text };
        }
        if (!res.ok) {
          throw new Error(
            typeof parsed === 'object' && parsed && 'detail' in (parsed as object)
              ? `HTTP ${res.status}: ${formatJson(parsed)}`
              : `HTTP ${res.status}`
          );
        }
        setHistoryFetchedRaw(formatJson(parsed));
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setHistoryFetchError(err instanceof Error ? err.message : 'Failed to load request');
      })
      .finally(() => {
        if (!ac.signal.aborted) setHistoryFetchLoading(false);
      });

    return () => ac.abort();
  }, [selectedHistoryId, serviceBase, apiKey]);

  const resetNewRequestView = useCallback(() => {
    pollAbortReasonRef.current = 'new-request';
    pollAbortRef.current?.abort();
    setPhase('idle');
    setStatusMessage('');
    setLastCallAt(null);
    setRawResult('');
    setParsedBody(null);
    setParseError(null);
    setFileLabel('');
    setSelectedHistoryId(null);
    setFileWasSent(false);
    setFileInputKey((k) => k + 1);
  }, []);

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setParseError(null);
    setParsedBody(null);
    setFileLabel('');
    if (!file) return;
    setFileLabel(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      try {
        setParsedBody(JSON.parse(text));
        setParseError(null);
      } catch {
        setParseError('Invalid JSON file.');
        setParsedBody(null);
      }
    };
    reader.readAsText(file);
  };

  const runSubmit = async () => {
    if (!serviceBase || !apiKey || parsedBody === null || parseError) return;

    pollAbortReasonRef.current = 'replace';
    pollAbortRef.current?.abort();
    const ac = new AbortController();
    pollAbortRef.current = ac;

    setPhase('submitting');
    setStatusMessage('Submitting POST /requests…');
    setRawResult('');
    setSelectedHistoryId(null);
    setFileWasSent(true);

    const kind = tryOutKind(slug);
    let activeRequestId: string | undefined;
    const postUrl = `${serviceBase.replace(/\/$/, '')}/requests`;

    try {
      const postRes = await fetch(postUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
        },
        body: JSON.stringify(parsedBody),
        signal: ac.signal,
      });
      const postAt = new Date().toISOString();
      setLastCallAt(postAt);

      const postText = await postRes.text();
      let postJson: unknown;
      try {
        postJson = postText ? JSON.parse(postText) : null;
      } catch {
        postJson = { _raw: postText };
      }

      if (!postRes.ok) {
        setRawResult(formatJson(postJson));
        setPhase('error');
        setStatusMessage(`Submit failed (${postRes.status}).`);
        void fetchQuota();
        return;
      }

      const postObj =
        postJson && typeof postJson === 'object' ? (postJson as Record<string, unknown>) : null;
      const rid =
        kind === 'boltz-3d-affinity'
          ? postObj?.parent_request_id
          : postObj?.request_id;
      if (typeof rid !== 'string' || !rid) {
        setRawResult(formatJson(postJson));
        setPhase('error');
        setStatusMessage('Response did not include a request id to poll.');
        void fetchQuota();
        return;
      }

      activeRequestId = rid;

      const entry: TryOutHistoryEntry = {
        id: rid,
        createdAt: postAt,
        lastStatus: 'polling',
      };
      prependHistory(slug, entry);
      setHistory(loadHistory(slug));
      setSelectedHistoryId(rid);

      setPhase('polling');
      setStatusMessage('Polling for status…');

      const { lastJson, lastCallAt: lastPoll } = await pollUntilDone(kind, rid, {
        baseUrl: serviceBase,
        apiKey,
        signal: ac.signal,
        onPoll: (body, t) => {
          setLastCallAt(t);
          if (selectedHistoryIdRef.current === rid) {
            setHistoryFetchedRaw(formatJson(body));
          }
          const o = body && typeof body === 'object' ? (body as Record<string, unknown>) : null;
          const st = o?.status;
          if (typeof st === 'string') {
            updateHistoryEntry(slug, rid, { lastStatus: st });
            setHistory(loadHistory(slug));
          }
        },
      });

      setLastCallAt(lastPoll);
      const finalRaw = formatJson(lastJson);
      setRawResult(finalRaw);
      if (selectedHistoryIdRef.current === rid) {
        setHistoryFetchedRaw(finalRaw);
      }
      setPhase('completed');
      setStatusMessage('Completed.');
      updateHistoryEntry(slug, rid, {
        lastStatus:
          lastJson && typeof lastJson === 'object' && 'status' in (lastJson as object)
            ? String((lastJson as Record<string, unknown>).status)
            : 'done',
      });
      setHistory(loadHistory(slug));
      void fetchQuota();
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        const reason = pollAbortReasonRef.current;
        pollAbortReasonRef.current = null;
        if (reason === 'replace' || reason === 'new-request') {
          return;
        }
        setPhase('idle');
        setStatusMessage('');
        return;
      }
      const msg = err instanceof Error ? err.message : 'Request failed.';
      setPhase('error');
      setStatusMessage(msg);
      setRawResult(msg);
      if (activeRequestId) {
        updateHistoryEntry(slug, activeRequestId, { lastStatus: 'error' });
        setHistory(loadHistory(slug));
      }
      void fetchQuota();
    }
  };

  if (!isTryOutSlug(slug)) {
    return <Navigate to={slug ? `/docs/${slug}` : '/docs'} replace />;
  }

  if (infoLoading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (infoError || !data) {
    return (
      <Layout>
        <Alert severity="error">Failed to load developer information.</Alert>
      </Layout>
    );
  }

  if (!selectedEndpoint || !serviceBase) {
    return (
      <Layout>
        <Alert severity="warning">
          This API is not available for your account or the endpoint could not be resolved.{' '}
          <Link to="/docs">Back to docs</Link>
        </Alert>
      </Layout>
    );
  }

  const busy = phase === 'submitting' || phase === 'polling';
  const isNewRequestView = selectedHistoryId === null;
  const disableUploadSubmit =
    busy || fileWasSent || historyFetchLoading || !isNewRequestView;
  const canSubmit =
    parsedBody !== null &&
    !parseError &&
    !busy &&
    !fileWasSent &&
    !historyFetchLoading &&
    isNewRequestView;

  const rawPanelText = (() => {
    if (selectedHistoryId) {
      if (historyFetchError) return historyFetchError;
      return historyFetchedRaw || '—';
    }
    if (phase === 'polling') {
      return 'Polling… The full response will appear when the job completes.';
    }
    if (phase === 'submitting') {
      return 'Submitting…';
    }
    return rawResult || '—';
  })();

  return (
    <Layout>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flex: 1,
          minHeight: 0,
          width: '100%',
          gap: 0,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: 240,
            flexShrink: 0,
            borderRight: 1,
            borderColor: 'divider',
            overflow: 'auto',
          }}
        >
          <Typography variant="subtitle2" sx={{ px: 2, py: 2 }} color="text.secondary">
            History
          </Typography>
          <List dense disablePadding>
            <ListItemButton
              selected={selectedHistoryId === null}
              onClick={resetNewRequestView}
              sx={{ py: 0.75 }}
            >
              <ListItemText primary="New request" primaryTypographyProps={{ fontWeight: 600 }} />
            </ListItemButton>
            {history.length === 0 ? (
              <Typography variant="body2" sx={{ px: 2, pb: 2, pt: 0.5 }} color="text.secondary">
                No runs yet.
              </Typography>
            ) : (
              history.map((h) => (
                <ListItemButton
                  key={h.id}
                  selected={selectedHistoryId === h.id}
                  onClick={() => setSelectedHistoryId(h.id)}
                  sx={{ py: 0.5, alignItems: 'flex-start' }}
                >
                  <ListItemText
                    primary={h.id.slice(0, 8) + '…'}
                    secondary={
                      <>
                        {new Date(h.createdAt).toLocaleString()}
                        {h.lastStatus ? ` · ${h.lastStatus}` : ''}
                      </>
                    }
                    primaryTypographyProps={{
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      sx: { wordBreak: 'break-all' },
                    }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItemButton>
              ))
            )}
          </List>
        </Paper>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, p: 2, gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h6">{tryOutPageTitle(slug)}</Typography>
              <Button component={Link} to={`/docs/${slug}`} size="small" sx={{ mt: 0.5 }}>
                Back to documentation
              </Button>
            </Box>
            <Paper variant="outlined" sx={{ px: 2, py: 1, minWidth: 220 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Quota (this API)
              </Typography>
              {quotaLoading && <CircularProgress size={20} />}
              {!quotaLoading && quotaError && (
                <Typography variant="body2" color="error">
                  Could not load quota
                </Typography>
              )}
              {!quotaLoading && quota && (
                <Typography variant="body2">
                  {quota.quota_used} / {quota.quota_max} used · {quota.quota_remaining} remaining
                </Typography>
              )}
            </Paper>
          </Box>

          <Typography variant="body2" whiteSpace="pre-wrap" sx={{ maxWidth: 800 }}>
            {tryOutInstructions(slug)}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
            <Button variant="outlined" component="label" disabled={disableUploadSubmit}>
              Upload JSON
              <input
                key={fileInputKey}
                type="file"
                accept="application/json,.json"
                hidden
                disabled={disableUploadSubmit}
                onChange={onFile}
              />
            </Button>
            {fileLabel && (
              <Typography variant="body2" color="text.secondary">
                {fileLabel}
              </Typography>
            )}
            <Button variant="contained" onClick={() => void runSubmit()} disabled={!canSubmit}>
              Submit and wait for result
            </Button>
          </Box>

          {parseError && <Alert severity="error">{parseError}</Alert>}

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Status
            </Typography>
            <Typography variant="body2">
              {phase === 'idle' && 'Ready.'}
              {phase === 'submitting' && statusMessage}
              {phase === 'polling' && statusMessage}
              {phase === 'completed' && statusMessage}
              {phase === 'error' && statusMessage}
            </Typography>
            {lastCallAt && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Last HTTP call: {new Date(lastCallAt).toLocaleString()}
              </Typography>
            )}
          </Paper>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Raw result
            </Typography>
            {selectedHistoryId && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                GET /requests/{selectedHistoryId}
              </Typography>
            )}
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 2,
                bgcolor: 'action.hover',
                borderRadius: 1,
                overflow: 'auto',
                maxHeight: 360,
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {selectedHistoryId && historyFetchLoading ? (
                <Box component="span" display="flex" alignItems="center" gap={1} sx={{ fontFamily: 'inherit' }}>
                  <CircularProgress size={18} />
                  Loading request status…
                </Box>
              ) : (
                rawPanelText
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Layout>
  );
}
